import React, { useState, useEffect, useRef } from 'react';
import { AdminBook, getJourneyType } from '../types';

interface BooksTableProps {
  books: AdminBook[];
  onViewDetails: (bookId: string) => void;
  onDelete: (bookId: string) => void;
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const ALL_COLUMNS = [
  { id: 'bookId',          label: 'Book ID',         defaultVisible: true },
  { id: 'title',           label: 'Title',            defaultVisible: true },
  { id: 'email',           label: 'Email',            defaultVisible: true },
  { id: 'phone',           label: 'Phone Number',     defaultVisible: false },
  { id: 'language',        label: 'Language',         defaultVisible: true },
  { id: 'journeyType',     label: 'Journey Type',     defaultVisible: true },
  { id: 'trimSize',        label: 'Trim Size',        defaultVisible: false },
  { id: 'pages',           label: 'Pages',            defaultVisible: true },
  { id: 'totalVersions',   label: 'Total Versions',   defaultVisible: false },
  { id: 'currentVersion',  label: 'Current Version',  defaultVisible: true },
  { id: 'status',          label: 'Status',           defaultVisible: true },
  { id: 'created',         label: 'Created',          defaultVisible: true },
  { id: 'lastUpdated',     label: 'Last Updated',     defaultVisible: false },
];

const STORAGE_KEY = 'admin_books_table_columns';

const getInitialVisibility = (): Record<string, boolean> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return Object.fromEntries(ALL_COLUMNS.map(c => [c.id, c.defaultVisible]));
};

const BooksTable: React.FC<BooksTableProps> = ({ books, onViewDetails, onDelete }) => {
  const [visible, setVisible] = useState<Record<string, boolean>>(getInitialVisibility);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visible));
  }, [visible]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleColumn = (id: string) => {
    setVisible(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (books.length === 0) {
    return <div className="px-6 py-12 text-center text-gray-500">No books found</div>;
  }

  return (
    <div>
      <div className="flex justify-end px-4 py-2 border-b border-gray-100">
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(o => !o)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Columns
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
              {ALL_COLUMNS.map(col => (
                <label key={col.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!visible[col.id]}
                    onChange={() => toggleColumn(col.id)}
                    className="accent-orange-500"
                  />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {visible.bookId        && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Book ID</th>}
              {visible.title         && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>}
              {visible.email         && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>}
              {visible.phone         && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone Number</th>}
              {visible.language      && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Language</th>}
              {visible.journeyType   && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Journey Type</th>}
              {visible.trimSize      && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trim Size</th>}
              {visible.pages         && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pages</th>}
              {visible.totalVersions && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Versions</th>}
              {visible.currentVersion && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Version</th>}
              {visible.status        && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>}
              {visible.created       && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>}
              {visible.lastUpdated   && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {books.map((book) => (
              <tr key={book._id} className="hover:bg-gray-50">
                {visible.bookId        && <td className="px-4 py-4 text-sm text-gray-500 font-mono" title={book._id}>...{book._id.slice(-6)}</td>}
                {visible.title         && <td className="px-4 py-4 text-sm text-gray-900 max-w-[180px] truncate" title={book.title}>{book.title || <span className="text-gray-400">—</span>}</td>}
                {visible.email         && <td className="px-4 py-4 text-sm text-gray-900">{book.user?.email || '-'}</td>}
                {visible.phone         && <td className="px-4 py-4 text-sm text-gray-900">{book.user?.phoneNumber || '-'}</td>}
                {visible.language      && <td className="px-4 py-4 text-sm text-gray-900">{book.language}</td>}
                {visible.journeyType   && <td className="px-4 py-4 text-sm text-gray-900">{getJourneyType(book)}</td>}
                {visible.trimSize      && <td className="px-4 py-4 text-sm text-gray-900">{book.bookSize || '-'}</td>}
                {visible.pages         && <td className="px-4 py-4 text-sm text-gray-900 text-center">{book.pageCount ?? <span className="text-gray-400">—</span>}</td>}
                {visible.totalVersions && <td className="px-4 py-4 text-sm text-gray-900 text-center">{book.totalVersions}</td>}
                {visible.currentVersion && <td className="px-4 py-4 text-sm text-gray-900 text-center">{book.currentVersion > 0 ? `v${book.currentVersion}` : '-'}</td>}
                {visible.status        && (
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[book.status] || 'bg-gray-100 text-gray-700'}`}>
                      {book.status.replace('_', ' ')}
                    </span>
                  </td>
                )}
                {visible.created       && <td className="px-4 py-4 text-sm text-gray-500">{new Date(book.createdAt).toLocaleDateString()}</td>}
                {visible.lastUpdated   && <td className="px-4 py-4 text-sm text-gray-500">{new Date(book.updatedAt).toLocaleDateString()}</td>}
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <button onClick={() => onViewDetails(book._id)} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Details</button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Permanently delete this book? This cannot be undone.\n\nBook ID: ...${book._id.slice(-6)}`)) {
                          onDelete(book._id);
                        }
                      }}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BooksTable;
